import { getGlobalHeadersAsObject } from "./global-headers";
import type { User } from "./types";

// Re-export types from types.ts for backwards compatibility
export type {
	Group,
	Permission,
	User,
	UserConfig,
	UserConfigProfile,
	UserConfigTodo,
	UserProfile,
	UserProfileGroup,
} from "./types";
export { EmailMethod } from "./types";

export const getUsers = (): Promise<User[]> => {
	return fetch(`/api/v2/users/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => res.json())
		.then((res) => res.results);
};

export const getUser = (userId: number): Promise<User> => {
	return fetch(`/api/v2/users/${userId}/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => res.json())
		.then((res) => res.results);
};

export const getCurrentUser = (): Promise<User> => {
	return fetch(`/api/v2/user/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => res.json())
		.then((res) => res.results)
		.then((res) => res[0]);
};

export const putUser = (user: User): Promise<User> => {
	return fetch(`/api/v2/users/${user.id}/`, {
		method: "PUT",
		body: JSON.stringify(user),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then(async (res) => {
		if (!res.ok) {
			const errorData = await res.json();
			throw new Error(errorData.detail || "Failed to save changes");
		}
		const data = await res.json();
		return data.results || data;
	});
};

export const setDefaultBoardConfig = (
	config: Record<string, unknown>
): Promise<{ message: string }> => {
	return fetch(`/api/v2/current-userprofile/set_default_board_config/`, {
		method: "POST",
		body: JSON.stringify({ config }),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then(async (res) => {
		if (!res.ok) {
			const errorData = await res.json();
			throw new Error(errorData.error || "Failed to save default board config");
		}
		return res.json();
	});
};

export interface UpdateRoleResponse {
	message: string;
	user: {
		id: number;
		username: string;
		is_staff: boolean;
		is_superuser: boolean;
	};
}

export const updateUserRole = (
	userId: number,
	isStaff: boolean,
	isSuperuser: boolean
): Promise<UpdateRoleResponse> => {
	return fetch(`/api/v2/users/${userId}/update_role/`, {
		method: "POST",
		body: JSON.stringify({
			is_staff: isStaff,
			is_superuser: isSuperuser,
		}),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then(async (res) => {
		if (!res.ok) {
			const errorData = await res.json();
			throw new Error(errorData.error || "Failed to update role");
		}
		return res.json();
	});
};

export interface AdminSetPasswordResponse {
	message: string;
	user: {
		id: number;
		username: string;
	};
}

export const adminSetPassword = (
	userId: number,
	newPassword: string
): Promise<AdminSetPasswordResponse> => {
	return fetch(`/api/v2/users/${userId}/admin_set_password/`, {
		method: "POST",
		body: JSON.stringify({
			new_password: newPassword,
		}),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then(async (res) => {
		if (!res.ok) {
			const errorData = await res.json();
			throw new Error(errorData.error || "Failed to set password");
		}
		return res.json();
	});
};

export interface EmailSignatureResponse {
	signature_html: string | null;
	signature_text: string | null;
	provider?: "gmail" | "outlook" | "imap";
	error?: string;
}

/**
 * Fetch the current user's email signature from their configured email provider.
 * - Gmail: Returns HTML and plain text signature from sendAs settings
 * - Outlook: May return signature if available (limited API support)
 * - IMAP: Returns null (no centralized signature storage)
 */
export const getEmailSignature = (): Promise<EmailSignatureResponse> => {
	return fetch(`/api/v2/current-userprofile/signature/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then(async (res) => {
		if (!res.ok) {
			return {
				signature_html: null,
				signature_text: null,
				error: "Failed to fetch signature",
			};
		}
		return res.json();
	});
};
